import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';
import apiService from 'v2/services/api';

import type { IDateService } from 'v2/interfaces/date';
import type {
  AddBankAccountModel,
  IAnticipationPromotion,
  IAvailable,
  IBankAccount,
  IBanklist,
  IBlockedExtract,
  ILoadAvailable,
  INewAccount,
  IOperationList,
  ISendTransfer,
  LoadAnticipation,
  SendTransferInvoice,
} from 'v2/interfaces/financial';

import { dateService } from './date';

import type { Observable } from 'rxjs';

interface IBankAccountParams {
  active: 'true';
}

interface Period {
  start: string;
  end: string;
}

class FinancialService {
  private exportPeriod$ = new Rx.BehaviorSubject<Period>(null);
  private banks$ = new Rx.BehaviorSubject<IBanklist[]>(null);

  constructor(private readonly dateService: IDateService) {}

  public listAvailable = (customerType: string): Observable<IAvailable> =>
    apiService.get(`/financial-statement/wallet?customerType=${customerType}`).pipe(map((response) => response.data));

  public listAvailableTransfer = (): Observable<ILoadAvailable> => {
    return apiService.get(`/anticipation/transfer${this.showNewFinancialStatement()}`).pipe(map((response) => response.data));
  };

  public listAnticipation = (): Observable<LoadAnticipation> => {
    return apiService.get(`/anticipation${this.showNewFinancialStatement()}`).pipe(map((response) => response.data));
  };

  public listBlockedExtract = (): Observable<IBlockedExtract[]> => {
    return apiService.get(`/extracts/blocked${this.showNewFinancialStatement()}`).pipe(map((response) => response.data));
  };

  public listBankAccounts = (params?: IBankAccountParams): Observable<IBankAccount[]> => {
    return apiService.get('/fundtransfers', params).pipe(map((response) => response.data));
  };

  public getBankAccount = (id: number): Observable<IBankAccount> => {
    return apiService.get(`/fundtransfers/${id}`).pipe(map((response) => response.data));
  };

  public verifyTransferByInvoice = (amount: number) => {
    return apiService.post('/transfers/check_amount_invoices', { amount }).toPromise();
  };

  public sendTransferByInvoice = (model: SendTransferInvoice): Observable<any> => {
    return apiService.post('/transfers/amount_invoices', model);
  };

  public sendTransfer = (model: ISendTransfer): Observable<any> => {
    return apiService.post(`/transfers${this.showNewFinancialStatement()}`, model);
  };

  public resendConfirmation = (uuid: string): Observable<any> => {
    return apiService.post(`fundtransfers/${uuid}/resend-confirmation`, {});
  };

  public makeMainAccount = (id: number): Observable<any> => {
    return apiService.post(`fundtransfers/${id}/make-main`, {});
  };

  public deleteAccount = (id: number): Observable<any> => {
    return apiService.delete(`fundtransfers/${id}`, {});
  };

  public listBanks = (internacional?: boolean): Observable<IBanklist[]> => {
    return apiService.get('/fundtransfers/banks', internacional ? { international: true } : {}).pipe(
      map((response) => {
        const banks = response.data
          ? Object.keys(response.data)
              .map((key) => ({ id: key, value: response.data[key] }))
              .sort((a, b) => Number(a.id) - Number(b.id))
          : [];
        this.banks$.next(banks);
        return banks;
      })
    );
  };

  public getBanks() {
    return this.banks$;
  }

  public listOperations = (): Observable<IOperationList[]> => {
    return apiService.get('/fundtransfers/operations').pipe(map((response) => response.data));
  };

  public createBankAccount = (model: INewAccount) => {
    type NewAccount = Omit<INewAccount, 'imgsIds'>;

    const { imgsIds, ...rest } = model;

    const newAccount: NewAccount = { ...rest };

    const bankCEFId = '104';

    if (newAccount.bank_id !== bankCEFId) {
      newAccount.operation_id = '';
    }

    if (imgsIds['imgDocCNH']) {
      newAccount.driver_license_image = imgsIds['imgDocCNH'];
    }

    if (imgsIds['imgDocCPF']) {
      newAccount.tax_document_image = imgsIds['imgDocCPF'];
    }

    if (imgsIds['imgDocRG']) {
      newAccount.personal_document_image = imgsIds['imgDocRG'];
    }

    if (imgsIds['videoDoc']) {
      newAccount.personal_document_video = imgsIds['videoDoc'];
    }

    if (imgsIds['imgBankReceipt']) {
      newAccount.address_image = imgsIds['imgBankReceipt'];
    }

    return apiService.post('/fundtransfers', newAccount).pipe(map((response) => response.data));
  };

  public addBankAccount = (model: AddBankAccountModel) => {
    return apiService
      .post('/fundtransfers', model)
      .pipe(map((response) => response.data))
      .toPromise();
  };

  public editBankAccount = (model: AddBankAccountModel) => {
    return apiService
      .put('/fundtransfers', model)
      .pipe(map((response) => response.data))
      .toPromise();
  };

  public verifyBankAccount = (banckAccountId: number, verificationCode: string) => {
    return apiService
      .post(`/fundtransfers/${banckAccountId}/verify-code`, { verification_code: verificationCode })
      .pipe(map((response) => response.data))
      .toPromise();
  };

  public resendVerificationCode = (banckAccountId: number) => {
    return apiService
      .post(`/fundtransfers/${banckAccountId}/resend-verification`, {})
      .pipe(map((response) => response.data))
      .toPromise();
  };

  public getAnticipationPromotion(): Observable<IAnticipationPromotion> {
    return apiService.get('/anticipation/promotions').pipe(map((response) => response.data));
  }

  public setExportPeriod(period: Period) {
    this.exportPeriod$.next(period);
  }

  public exportBackground(email: string) {
    const period = this.exportPeriod$.value;
    const params = {
      start_date: period.start,
      final_date: period.end,
    };
    if (email) {
      Object.assign(params, {
        email,
      });
    }
    const endPeriodDateFormat = this.dateService.stringToDate(period.end);
    const isFutureDate = this.dateService.isFutureDate(endPeriodDateFormat);
    if (isFutureDate) {
      params.final_date = this.dateService.dateToString(new Date());
    }
    return apiService.post(`reports/export/financial-statement${this.showNewFinancialStatement()}`, params);
  }

  public checkHasCommissions(): Promise<boolean> {
    return apiService
      .get('/financial-v2/commissions/exists-for-customer')
      .toPromise()
      .then((res) => !!res.data.exists);
  }

  private showNewFinancialStatement() {
    const params = new URL(document.location.href).searchParams;
    const showNew = !!params.get('new-financial-statement') && params.get('new-financial-statement') === 'true';

    return !!showNew ? '?new_financial_statement=true' : '';
  }
}

const financialService = new FinancialService(dateService);
export default financialService;
